import { ClipboardList, Users, Search, User, NotebookPen, Menu, House } from "lucide-react";
import DiaryPenIcon from "@/components/shared/DiaryPenIcon";
import { useLocation, useNavigate } from "react-router";
import type React from "react";
import { DIARY_HOME_LABEL } from "@/hooks/useMyPageLabel";
import { useTenantFeatureFlag } from "@/hooks/useTenantFeatureFlag";
import { useHome } from "@/hooks/useHome";
import { useOnboarding } from "@/hooks/useOnboarding";
import { path } from "@/utils/common";
import { withNavJump } from "@/lib/nav/backTracker";
import {
  ROUTE_MEETINGS_LIST,
  ROUTE_MEMO,
  ROUTE_MY_CUSTOMERS,
  ROUTE_MYPAGE,
} from "@/utils/const";

/**
 * PRODUCT_DEV-583: Ph1.80「新括り」下部ナビ。
 * 商談一覧 / 顧客一覧(マイ顧客) / 検索 / 録音 / メニュー の5タブ。
 * 通知は本タブから外し、ヘッダーのベル(Ph180BellButton)へ移設する。
 * 「録音」は既存メモタブ(/memo)のラベル変更(id/導線は memo のまま)。
 *
 * MobileTabBar と同じ props/検索オーバーレイ挙動を踏襲し、OFF↔ON で下部ナビだけが差し替わる。
 *
 * PRODUCT_DEV-675: `scheduling` フラグ ON のテナントは**3枠目が「検索」ではなく「マイページ」**になる
 * （中山さんのプロトタイプ準拠）。日程調整・活動サマリ・日報はすべてマイページの中にあり、
 * これまでマイページへはメニューシート経由でしか行けなかったため、下部ナビに常設する。
 * 追い出した検索は商談一覧(ホーム)の「閲覧 / 保存」ピル行へ移す(HomeInfoLayer)。
 * フラグ OFF のときは従来どおり「検索」タブのまま（回帰なし）。
 *
 * PRODUCT_DEV-782: `diaryHome` フラグ ON のテナントは**4タブ**（手帳 / 商談一覧 / 顧客一覧 /
 * メニュー）になり、**先頭が手帳**になる。アプリを開いて最初に出るのが手帳、という 2d案の
 * 思想をナビでも通すため。
 *
 * PRODUCT_DEV-949: タブ名を「商談記録」→「商談一覧」に変え、アイコンを家（ホームの名残り）から
 * クリップボードの一覧に、手帳を開いた本から「分厚い手帳＋筆ペン」（`DiaryPenIcon`）に替えた。
 * 顧客一覧（人）・手帳（本）と形が被らないこと、吹き出しを使わないことが条件。
 *
 * ⚠ **商談一覧の URL（`/`）は動かさない。** 絞り込みを URL に載せる作りで、通知・共有リンク・
 * ブックマークがこの URL を指している。ホームの入れ替えはタブの並びと着地先で行う。
 *
 * ⚠ **消える2つの行き先を確かめてある。** 「検索」は商談一覧の「閲覧 / 保存」ピル行から、
 * 「録音（議事録・メモ）」は手帳のメモタイルの「すべて見る」から入れる。どちらも辿れなく
 * なってはいない。
 *
 * PRODUCT_DEV-1092: マネージャー画面への切替は**ここには置かない**（名前の行の右端。`ViewIdentityHeader`）。
 * 10/8 の版は左端に「⇄ マネージャー画面」を足して5つにしていたが、左端は HOME に戻る場所として
 * 手が覚えていて押し間違えるので外した（2026-10-13 ユーザ決定）。マネージャー画面側のナビは `ManagerTabBar`。
 */
interface Ph180TabBarProps {
  onMenuClick: () => void;
  onSearchClick?: () => void;
  isSearchOpen?: boolean;
  onCloseSearch?: () => void;
}

const HOME_PATH = path("/");

const Ph180TabBar: React.FC<Ph180TabBarProps> = ({
  onMenuClick,
  onSearchClick,
  isSearchOpen,
  onCloseSearch,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // 下のタブからの移動は「← はひとつ前の画面」にする印を付ける（PRODUCT_DEV-1159）
  const go = (to: string) => navigate(to, { state: withNavJump() });
  const schedulingEnabled = useTenantFeatureFlag("scheduling");
  const diaryHomeEnabled = useTenantFeatureFlag("diaryHome");
  // PRODUCT_DEV-1070: HOME のタブに小さな家を添える（手帳を使っている会社だけ）
  const home = useHome();
  const homeTabKey = diaryHomeEnabled
    ? home.screen === "diary"
      ? "diary"
      : home.screen === "meetings"
        ? "home"
        : null // 活動サマリはマネージャー画面のナビ（ManagerTabBar）に家を付ける
    : null;
  // PRODUCT_DEV-783: メニューの「はじめての方へ」を1度開くまで、メニューのタブにも赤ポチ（行が見えないため）
  const { showNewDot: onboardingDot } = useOnboarding();

  const onDashboard = pathname === HOME_PATH;
  const onMyCustomers = pathname.startsWith(ROUTE_MY_CUSTOMERS);
  const onMyPage = pathname.startsWith(ROUTE_MYPAGE);

  // 検索オーバーレイが開いている間に他タブをタップしたら、まず overlay を閉じる。
  const wrapWithCloseSearch = (action: () => void) => () => {
    if (isSearchOpen) onCloseSearch?.();
    action();
  };

  type Tab = {
    key: "home" | "mycustomers" | "search" | "mypage" | "memo" | "menu" | "diary";
    label: string;
    icon: typeof ClipboardList;
    active: boolean;
    onClick: () => void;
  };

  // 3枠目だけがフラグで入れ替わる。他の4枠は ON/OFF で同じ位置・同じ挙動。
  const thirdTab: Tab = schedulingEnabled
    ? {
        key: "mypage",
        label: "マイページ",
        icon: User,
        active: onMyPage,
        onClick: wrapWithCloseSearch(() => go(ROUTE_MYPAGE)),
      }
    : {
        key: "search",
        label: "検索",
        icon: Search,
        active: !!isSearchOpen,
        onClick: () => {
          if (onDashboard) {
            if (isSearchOpen) {
              onCloseSearch?.();
            } else {
              onSearchClick?.();
            }
          } else {
            go(`${HOME_PATH}?showFilter=true`);
          }
        },
      };

  /** 手帳（PRODUCT_DEV-782）。先頭に置く。 */
  const diaryTab: Tab = {
    key: "diary",
    label: DIARY_HOME_LABEL,
    icon: DiaryPenIcon,
    active: onMyPage,
    onClick: wrapWithCloseSearch(() => go(ROUTE_MYPAGE)),
  };

  const tabs: Tab[] = diaryHomeEnabled
    ? [
        diaryTab,
        {
          key: "home",
          label: "商談一覧",
          icon: ClipboardList,
          // `/` は手帳へ送られるので、一覧に必ず当たる URL を指す
          active: pathname === ROUTE_MEETINGS_LIST || pathname === HOME_PATH,
          onClick: wrapWithCloseSearch(() => go(ROUTE_MEETINGS_LIST)),
        },
        {
          key: "mycustomers",
          label: "顧客一覧",
          icon: Users,
          active: onMyCustomers,
          onClick: wrapWithCloseSearch(() => go(ROUTE_MY_CUSTOMERS)),
        },
        {
          key: "menu",
          label: "メニュー",
          icon: Menu,
          active: false,
          onClick: wrapWithCloseSearch(onMenuClick),
        },
      ]
    : [
    {
      key: "home",
      label: "商談一覧",
      icon: ClipboardList,
      // PRODUCT_DEV-996: 議事録タブ（`/meetings-list?list=minutes`）も商談一覧なので点ける
      active: pathname === HOME_PATH || pathname === ROUTE_MEETINGS_LIST,
      onClick: wrapWithCloseSearch(() => go(HOME_PATH)),
    },
    {
      key: "mycustomers",
      label: "顧客一覧",
      icon: Users,
      active: onMyCustomers,
      onClick: wrapWithCloseSearch(() => go(ROUTE_MY_CUSTOMERS)),
    },
    thirdTab,
    {
      key: "memo",
      // PRODUCT_DEV-996: 議事録を商談一覧へ移し、メモとToDoだけの画面になった
      label: "メモ・ToDo",
      icon: NotebookPen,
      active: pathname === ROUTE_MEMO,
      onClick: wrapWithCloseSearch(() => go(ROUTE_MEMO)),
    },
    {
      key: "menu",
      label: "メニュー",
      icon: Menu,
      active: false,
      onClick: wrapWithCloseSearch(onMenuClick),
    },
  ];

  // PRODUCT_DEV-981: bottom-0 ではなく fixed-bottom-bar（Android でスクロールするとナビバーの裏へ下がるため。index.css）
  return (
    <nav className="fixed-bottom-bar left-0 right-0 bg-white border-t border-neutral-200 z-40">
      <div className={`grid h-16 ${diaryHomeEnabled ? "grid-cols-4" : "grid-cols-5"}`}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const color = tab.active ? "text-primary" : "text-neutral-500";
          const weight = tab.active ? "font-bold" : "font-normal";
          return (
            <button
              key={tab.key}
              onClick={tab.onClick}
              className="flex flex-col items-center justify-center gap-0.5 transition-colors hover:text-neutral-700"
            >
              <span className="relative">
                <Icon size={22} className={color} strokeWidth={tab.active ? 2.25 : 2} />
                {tab.key === "menu" && onboardingDot && (
                  <span
                    className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-danger ring-2 ring-white"
                    aria-label="新しい案内があります"
                    role="status"
                  />
                )}
              </span>
              {/* PRODUCT_DEV-1070: HOME の印。家はラベルの外（左）にはみ出させ、
                  付いていてもいなくてもラベルがアイコンの真下からずれないようにする */}
              <span className={`relative text-[10px] ${color} ${weight}`}>
                {tab.key === homeTabKey && (
                  <House
                    size={10}
                    strokeWidth={2.5}
                    aria-hidden
                    className="absolute right-full top-1/2 -translate-y-1/2 mr-0.5 text-neutral-400"
                  />
                )}
                {tab.label}
                {tab.key === homeTabKey && <span className="sr-only">（HOME）</span>}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Ph180TabBar;
